import React, {
    useCallback,
    useMemo,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
    usePagination,
    useDidUpdate,
    useMainContainerQuery,
    useBackToState,
} from 'bdvp-common/src/hooks';
import NotificationRenderer from 'bdvp-common/src/components/NotificationRenderer';
import { getMediaPath } from 'bdvp-common/src/utils';
import { QUERY_PARAMS } from 'bdvp-common/src/utils/constants';
import { Tracking } from 'bdvp-tracking';
import { useMediaConfigSearch } from 'bdvp-common/src/hooks/config';

import Tags from '../../containers/Tags';
import Message from '../../containers/Message';
import SortModal from '../../containers/SortModal';
import SortButton from '../../containers/SortButton';
import SortDropdown from '../../containers/SortDropdown';
import useIsFilterInModal from '../../hooks/useIsFilterInModal';
import toggleFiltersParam from '../../utils/toggleFiltersParam';
import { LIMIT, QUERY_TYPE } from '../../utils/constants';
import SectionTemplate from '../SectionTemplate';
import CardsGrid from '../CardsGrid';
import Notification from '../Notification';
import Heading from '../Heading';

export const getSearchParamsWithPage = (searchParams, index, limit) => {

    if(!searchParams) {

        return '';

    }

    return `${searchParams}&p=${Math.floor(index/limit)}`;

};

const SearchResults = ({
    loading,
    data,
    error,
    page,
    fetchMoreData,
    loadingMore,
    searchQuery,
    resetSort,
    isSorting,
    setIsSorting,
    searchSortSelectedOption,
    contentPreference,
    queryType,
    locationSearchWasUpdated,
    showFilters,
    isFilterActive,
}) => {

    const backToState = useBackToState('back_to_results');
    const config = useMediaConfigSearch();
    const isFilterInModal = useIsFilterInModal();
    const history = useHistory();

    const {
        limit,
        sizes,
        isSortInModal,
    } = useMainContainerQuery(
        [
            'XL',
            'LG',
            'MD',
            'SM',
            'XS',
        ],
        [
            {
                limit: 35,
                sizes: {
                    xs: 20,
                },
                isSortInModal: false,
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 25,
                },
                isSortInModal: false,
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 33.33,
                },
                isSortInModal: false,
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 50,
                },
                isSortInModal: true,
            },
            {
                limit: LIMIT,
                sizes: {
                    xs: 100,
                },
                isSortInModal: true,
            },
        ],
        {
            limit: LIMIT,
            sizes: {
                xs: 100,
                sm: 50,
                md: 33.33,
                lg: 25,
                xl: 20,
            },
            isSortInModal: true,
        }
    );

    const entriesSoFar = (page + 1) * limit;
    const totalResults = data?.search?.total ?? 0;
    const hasMore = totalResults > 0 && entriesSoFar < totalResults;
    const queryTypeParam = queryType ? `&${QUERY_PARAMS.queryType}=${queryType}`: '';
    const searchParams = searchQuery
        ? `?q=${encodeURIComponent(searchQuery)}&cp=${contentPreference}${queryTypeParam}`
        : '';
    const list = useMemo(() => {

        const searchResultsList = data?.search?.plays ?? [];

        return searchResultsList.map((item) => item?.mediaPlayback?.[0] ?? {});

    }, [data]);
    const {
        paginatedList,
        hasMoreItems,
        getMoreItems,
        resetPagination,
        loadingPagination: isLoading,
    } = usePagination({
        list,
        limit,
        hasMore,
        fetchMore: fetchMoreData,
        isLoading: loading,
    });

    const contentCardClickHandler = useCallback((event, item, index) => {

        event.preventDefault();
        Tracking.trackSearchPageOnItemClick(event, index);

        const mediaPath = getMediaPath(item);

        history.push(
            {
                pathname: `/${mediaPath}`,
                search: getSearchParamsWithPage(searchParams, index, LIMIT),
            },
            backToState
        );

    }, [
        history,
        searchParams,
        backToState,
    ]);

    const prevSearchQuery = useRef('');

    const cardFocusIndex = useRef(null);

    useEffect(() => {

        if(!loading && !error && prevSearchQuery.current !== searchQuery) {

            const dataTotal = data?.search?.total;

            if(
                locationSearchWasUpdated
                && queryType === QUERY_TYPE.freetext
                && dataTotal
            ) {

                cardFocusIndex.current = 0;

            }

            if(queryType === QUERY_TYPE.freetext && dataTotal === 0) {

                Tracking.trackFreeTextSearchNoResults();

            }

            prevSearchQuery.current = searchQuery;

            Tracking.trackSearchEvents(searchQuery, dataTotal);

        }

    }, [
        data,
        loading,
        error,
        searchQuery,
        queryType,
        locationSearchWasUpdated,
    ]);

    useDidUpdate(() => {

        resetPagination();

    }, [searchQuery]);

    useEffect(() => {

        return () => {

            resetSort();

        };

    }, [resetSort]);

    useDidUpdate(() => {

        setIsSorting(false);

    }, [data]);

    const searchParamsCallback = useCallback((index) => getSearchParamsWithPage(searchParams, index, LIMIT), [searchParams]);

    useEffect(() => {

        const urlParams = new URLSearchParams(history.location.search);
        if (urlParams.get(QUERY_PARAMS.openFilters) && !isFilterInModal) {

            showFilters();

        }

    }, []);

    useEffect(() => {

        toggleFiltersParam(isFilterActive, history);

    }, [
        isFilterActive,
        history,
    ]);

    if (error) {

        return <Message type={'search_error'} />;

    }

    if (totalResults === 0 && !loading) {

        return <Message type={'no_results'} />;

    }

    const sort = isSortInModal ? <SortButton /> : <SortDropdown />;

    const totalResultsText = `${totalResults.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} Result${totalResults > 1 ? 's' : ''}`;

    const seeMoreButtonHandler = () => {

        getMoreItems();
        Tracking.trackSearchResultsPageOnSeeMoreClick();

    };

    const body = isLoading ? (
        <CardsGrid
            orientation={'portrait'}
            isLoading
            limit={limit}
            sizes={sizes}
        />
    ) : (
        <CardsGrid
            handleItemClick={contentCardClickHandler}
            hasMore={hasMoreItems}
            limit={limit}
            list={paginatedList}
            loadingMore={loadingMore}
            loadMoreButtonHandler={seeMoreButtonHandler}
            orientation={'portrait'}
            sizes={sizes}
            stat={searchSortSelectedOption?.resultsDisplay}
            searchParams={searchParamsCallback}
            cardFocusIndex={cardFocusIndex.current}
        />
    );

    return (
        <SectionTemplate
            heading={<Heading isLoading={loading && !isSorting}>{totalResultsText}</Heading>}
            notification={
                <NotificationRenderer
                    config={config}
                    render={(id, contentConfig) => (
                        <Notification id={id} contentConfig={contentConfig} />
                    )}
                />
            }
            sort={sort}
            modals={isSortInModal ? <SortModal /> : null}
            tags={<Tags />}
            body={body}
        />
    );

};

SearchResults.propTypes = {
    fetchMoreData: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    resetSort: PropTypes.func.isRequired,
    isSorting: PropTypes.bool.isRequired,
    setIsSorting: PropTypes.func.isRequired,
    data: PropTypes.object,
    error: PropTypes.object,
    loading: PropTypes.bool,
    loadingMore: PropTypes.bool,
    searchQuery: PropTypes.string,
    searchSortSelectedOption: PropTypes.object,
    contentPreference: PropTypes.string.isRequired,
    queryType: PropTypes.string,
    locationSearchWasUpdated: PropTypes.bool,
    showFilters: PropTypes.func.isRequired,
    isFilterActive: PropTypes.bool.isRequired,
};

SearchResults.defaultProps = {
    isShareModalOpen: false,
    searchQuery: null,
    queryType: null,
    locationSearchWasUpdated: false,
};

export default SearchResults;
